import React from 'react';
import { Link } from 'react-router-dom';
import { FaHome, FaChevronRight, FaSyncAlt, FaNetworkWired, FaServer, FaCheckCircle, FaArrowRight, FaPhone, FaCogs, FaShieldAlt, FaRocket } from 'react-icons/fa';
import Navigation from '../components/Navigation';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';

const NetworkRefreshPage = () => {
    const services = [
        {
            icon: <FaSyncAlt size={24} />,
            title: 'Network Modernization',
            description: 'Upgrade legacy infrastructure to modern, high-performance network solutions.',
            gradient: 'from-blue-500 to-cyan-500'
        },
        {
            icon: <FaNetworkWired size={24} />,
            title: 'Switch & Router Upgrades',
            description: 'Replace aging switches and routers with next-generation equipment.',
            gradient: 'from-purple-600 to-indigo-600'
        },
        {
            icon: <FaServer size={24} />,
            title: 'Infrastructure Assessment',
            description: 'Comprehensive evaluation of existing infrastructure to plan optimal upgrades.',
            gradient: 'from-orange-500 to-red-500'
        },
        {
            icon: <FaShieldAlt size={24} />,
            title: 'Security Updates',
            description: 'Enhance security posture with latest firmware and security configurations.',
            gradient: 'from-green-500 to-teal-500'
        },
        {
            icon: <FaCogs size={24} />,
            title: 'Configuration Migration',
            description: 'Seamless migration of configurations with minimal downtime.',
            gradient: 'from-pink-500 to-rose-500'
        },
        {
            icon: <FaRocket size={24} />,
            title: 'Performance Optimization',
            description: 'Fine-tune network for maximum speed and reliability.',
            gradient: 'from-amber-500 to-orange-500'
        }
    ];

    const benefits = [
        'Improved network performance',
        'Enhanced security posture',
        'Reduced maintenance costs',
        'Better scalability',
        'Minimal downtime',
        'Future-proof infrastructure'
    ];

    return (
        <div className="font-sans">
            <TopBar />
            <Navigation />

            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-gray-900 via-blue-900 to-cyan-900 py-24 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                </div>

                <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl opacity-20 translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500 rounded-full filter blur-3xl opacity-20 -translate-x-1/2 translate-y-1/2"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <nav className="flex items-center gap-2 text-sm text-gray-300 mb-8">
                        <Link to="/" className="flex items-center gap-1 hover:text-white transition-colors">
                            <FaHome size={14} />
                            <span>Home</span>
                        </Link>
                        <FaChevronRight size={10} className="text-gray-500" />
                        <Link to="/services/national-rollouts" className="hover:text-white transition-colors">National Rollouts</Link>
                        <FaChevronRight size={10} className="text-gray-500" />
                        <span className="text-cyan-400">Network Refresh & Upgrade</span>
                    </nav>

                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Network Refresh</span> & Upgrade
                            </h1>
                            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                                Modernize your network infrastructure across all locations with coordinated upgrades and minimal downtime.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300">
                                    Get Started
                                    <FaArrowRight size={14} />
                                </Link>
                                <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                                    <FaPhone size={14} />
                                    Call Us Now
                                </a>
                            </div>
                        </div>
                        <div className="relative">
                            <div className="bg-gradient-to-br from-blue-600/20 to-cyan-600/20 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                                <img
                                    src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&h=400&fit=crop"
                                    alt="Network Refresh"
                                    className="w-full h-80 object-cover rounded-xl shadow-2xl"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">Our Services</span>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Complete Network Refresh Solutions</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            End-to-end network modernization services for multi-site deployments
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {services.map((service, index) => (
                            <div
                                key={index}
                                className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-blue-200"
                            >
                                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                    {service.icon}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{service.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <img
                                src="https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=600&h=500&fit=crop"
                                alt="Network Infrastructure"
                                className="w-full h-auto rounded-2xl shadow-2xl"
                            />
                        </div>
                        <div>
                            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">Why Upgrade</span>
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Benefits of Network Refresh</h2>
                            <p className="text-lg text-gray-600 mb-8">
                                A modern network infrastructure is the foundation for business success in the digital age.
                            </p>
                            <div className="grid grid-cols-2 gap-4">
                                {benefits.map((benefit, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <FaCheckCircle className="text-blue-500 flex-shrink-0" size={20} />
                                        <span className="text-gray-700 font-medium">{benefit}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-cyan-600">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Modernize Your Network?</h2>
                    <p className="text-xl text-blue-100 mb-8">
                        Get a free network assessment and upgrade roadmap today.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-blue-600 rounded-xl font-semibold hover:shadow-lg transition-all duration-300">
                            Request Assessment
                            <FaArrowRight size={14} />
                        </Link>
                        <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                            <FaPhone size={14} />
                            Call Now
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default NetworkRefreshPage;
